import { Close, Person } from "@mui/icons-material";
import { Box, capitalize, Divider, IconButton, Modal, Skeleton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Table, usePagination } from "@frontend/features/common/components/Table";
import { ModalBox } from "@frontend/features/common/utils/modals";
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { useDebouncedState } from "@frontend/features/common/hooks/useDebouncedState";

import { useGetUsersByAccess } from "@frontend/features/users/queries";

export interface UserSelectorTableItem {
  name: string,
  id: string,
  agency: string,
  phone: string,
  clientId: string
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (userTableItem: UserSelectorTableItem) => void;
  agencyId: number;
  selectedItemId?: string | number;
}

export const UserSelectorModal = ({ open, onClose, onSelect, agencyId, selectedItemId }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useDebouncedState('', 500);;
  const pagination = usePagination();

  const { data: users, isLoading } = useGetUsersByAccess({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search,
    agencyId: String(agencyId)
  });
  

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="agent-modal-selector-title">
      <ModalBox display="flex" flexDirection="column" gap={theme.spacing(2)}>
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <Person color="disabled" />
            <Typography id="agent-modal-selector-title" variant="h6" component="h2">
            {t('users.selector.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close color="primary" />
          </IconButton>
        </Box>
        <Divider />

        <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
          <Typography
            variant="subtitle1"
            align="left"
            color={theme.palette.text.primary}
            sx={{
              flexGrow: 1,
            }}
          >
            {users?.count ?? 0} {t('users.results')}
          </Typography>
          <SearchInput placeholder={t('users.search')} onSearch={setSearch} />

        </Box>

        {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}

        {users && <Table
          selectedItemId={selectedItemId}
          onRowSelect={onSelect}
          columns={[
            {
              id: 'name',
              label: t('users.table.name'),
              align: 'left',
            },
            {
              id: 'agency',
              label: t('users.table.agency'),
              align: 'left',
            },
            { id: 'email', label: t('users.table.email'), align: 'left' },
          ]}
          data={{
            key: 'email',
            items: users?.items.map((user) => ({
              name: `${capitalize(String(user.firstName))} ${capitalize(String(user.lastName))}`,
              id: user.id,
              agency: user.agencyName,
              email: user.email,
              clientId: user.clientId
            })),
            totalCount: users.count,
          }}
          pagination={pagination}
          stickyHeader
        />}

      </ModalBox>
    </Modal>
  );
};
